// ChequeV2 — batch 3's rebuild of Manager's cheque, stage 1 (the deposit) (UI rebuild; drawn when lib/ui-v2.ts switches
// batch 3 on). The cheque's progress (/mc/:dealId, McDeal.tsx) is not this file's.
//
// ASTRA'S SHAPE (operator's ruling, 26 Oct 2026): one card — Customer and Seller, Face amount and the deposit, where the
// deposit lands and the two fees — the prefill percent and notes under More, then "Review deposit", which opens the
// readback (the summary card's figures, beside the card from lg, inside it on the phone) and turns the primary into
// the booking press. On a coin deposit the conversion is two more boxes, Rate and the coin figure, under the fees.
//
// THE STATE MACHINE IS ManagerCheque's, COPIED VERBATIM (batch 3 brief): the stable deal id per form fill, the
// needs_confirm create-party / create-seller answers and their re-POST, the three-way deposit calculator with its
// prefill, typed-AED and chain-locked rules, whole-bps fees, the fee and FX refusals, canSubmit, and the payload. The
// summary rows are the old screen's own helper (chequeSummaryRows), imported. Only what is shown, in what order and
// grouped how is new — and one UI flag, `reviewing`, which decides whether the primary asks for the review or books.
import { useState, useMemo, useEffect, useRef } from 'react';
import { ApiError, isSentinel } from '../../lib/errors';
import { RepeatPanel, useRepeatGuard } from '../../components/RepeatPanel';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { tg } from '../../lib/telegram';
import { Combobox, type ComboboxOption, NO_PARTY_CREATE_HINT } from '../../components/Combobox';
import { rosterCut, usePartySearch } from '../../lib/roster-cut';
import { RailPicker, type RailValue } from '../../components/RailPicker';
import { MoreDetails } from '../../components/MoreDetails';
import { Field } from '../../components/Field';
import { ErrorLine } from '../../components/ErrorLine';
import { KeyValue, type KeyValueItem } from '../../components/KeyValue';
import { fmtAmount, fmtTime, displayAsset } from '../../lib/format';
import { partyName, partySubtitle, isStale, needsNameReview, rankForQuery, foldAsset, sameAsset } from '../../lib/naming';
import { DEFAULT_RAILS, newChequeId, isFiat } from '../../lib/workflow';
import { useTradeMath } from '../../hooks/useTradeMath';
import { fmtQty } from '../../lib/trade-math';
import { depositFacts } from '../../lib/mc-deposit';
import { readState } from '../../lib/read-state';
import { chequeSummaryRows } from '../ManagerCheque';
import { PageHead, ReadFailed, ServerLine, StateLine } from '../../components/v2/kit';
import { FormCard, FormGrid, FormPage, BeforeConfirming, railWord } from '../../components/v2/broker/form';
import { AboutFold } from '../../components/v2/record/parts';
import { FigureRow } from '../../components/v2/record/BalanceRows';
import { ReceiptAmount } from '../../components/v2/Receipt';

/** The seeds a deposit is usually asked at; "Full" is the whole cheque, face + commission (ManagerCheque.tsx). */
const PREFILL_CHIPS = [['2', '2%'], ['10', '10%'], ['25', '25%'], ['50', '50%'], ['100', 'Full']] as const;

/** Where a manager's-cheque leg may sit — ManagerCheque's MC_PAY_IN_KINDS, the same literal (that file exports only
 *  the pay-out list). No bank.*, no legacy.*; enforced server-side in manager-cheque.ts::isCustomerPayRailAllowed. */
const MC_PAY_IN_KINDS     = ['cash', 'angadiya', 'exchange', 'crypto', 'hot', 'cold'];

// Defaults, in PERCENT (§6 cheque fee unit) — ManagerCheque's.
const DEPOSIT_PCT_DEFAULT      = '2';
const CUSTOMER_FEE_PCT_DEFAULT = '0.80';
const SELLER_FEE_PCT_DEFAULT   = '0.40';
const SELLER_FEE_PCT_KEY       = 'mc.sellerFeePct';   // per-device convenience only; the deal carries the figure

/** The seller fee last used on this device, when there is one. */
function lastSellerFeePct(): string {
  try {
    const v = localStorage.getItem(SELLER_FEE_PCT_KEY);
    if (v && Number(v) > 0) return v;
  } catch { /* no storage — the default stands */ }
  return SELLER_FEE_PCT_DEFAULT;
}

export function ChequeV2() {
  const nav = useNavigate();

  // Customer (lazy-create flow same as Book Trade)
  const [customerSlug, setCustomerSlug]   = useState('');
  const [pendingProfileChoice, setPending] = useState<null | { slug: string; display_name: string }>(null);
  // MC seller lazy-create — role picker shown when seller is new
  const [pendingSellerChoice, setPendingSeller] = useState<null | { slug: string; display_name: string }>(null);

  // Face + seller
  const [faceAmount, setFaceAmount]   = useState('');
  const [mcSellerSlug, setMcSeller]   = useState('');

  // ARRIVING FROM A CHAIN ROW (sweep item 31, 14 Sept 2026): ?rail= and
  // ?railAsset= name the wallet the deposit reached, ?take= the coins it
  // brought, ?note= the chain hash. The coin box is then LOCKED to the
  // chain's figure — the coins that arrived are the fact; the rate and the
  // AED are worked from them — so a cheque booked from the chain can never
  // disagree with it (a 70,000 USDT deposit that reached the books as
  // 69,995.41, sweep 3.1 #2). ?lock=coin is the same contract the Chain
  // screen hands the trade screen.
  const [search] = useSearchParams();
  const chainRail: RailValue | null = search.get('rail') && search.get('railAsset')
    ? { slug: search.get('rail')!, asset: search.get('railAsset')! } : null;
  const chainTake   = search.get('take') ?? '';
  // The lock belongs to the wallet the chain row named: move the pay-in rail
  // to another asset and the lock lets go (review, 14 Sept 2026 — a rail
  // switch used to leave a read-only, empty coin box behind).
  const chainLockable = !!chainRail && chainTake !== '' && Number(chainTake) > 0;

  // Customer pay-in rail (deposit lands here)
  const [customerRail, setCustomerRail] = useState<RailValue>(chainRail ?? { ...DEFAULT_RAILS.cash });
  const chainLocked = chainLockable && !!chainRail && sameAsset(customerRail.asset, chainRail.asset);

  // The three percentages, on screen from the start (§5.3 — changed on
  // every real cheque), typed in percent.
  const [depositPct, setDepositPct]         = useState(DEPOSIT_PCT_DEFAULT);
  const [customerFeePct, setCustomerFeePct] = useState(CUSTOMER_FEE_PCT_DEFAULT);
  const [sellerFeePct, setSellerFeePct]     = useState(lastSellerFeePct);
  const [notes, setNotes]                   = useState(search.get('note') ?? '');

  // THE DEPOSIT GROUP: deposit in AED, rate, deposit in the rail's asset. The
  // shared calculator owns all three (any two fill the third; amounts outrank
  // the rate; the rate reads the way the pair is quoted — "AED per USDT",
  // "INR per AED"). Required whenever the deposit rail isn't AED: the cheque
  // math is AED-denominated, and without a conversion the form used to relabel
  // AED amounts as USDT (2,000 AED deposit shown/booked as 2,000 USDT —
  // 2026-08-11 parallel-run finding). When the rail IS AED the group is not
  // shown and the hook sits idle.
  const math = useTradeMath(customerRail.asset, 'AED');
  // The AED box is prefilled with X% of face + commission (through preset(),
  // so it counts as the first amount on the screen and anchors when a rate is
  // pasted) and keeps following face/% until the operator types in that box
  // themselves — a figure they typed is the customer's, not ours to overwrite.
  // Blanking the box hands it back to the computed figure on the next change.
  const depositTyped = useRef(false);

  // ── data ────────────────────────────────────────────────────────────────
  const customers = usePartySearch({
    base: ['parties', 'customer'], text: customerSlug,
    ask: (q) => api.listParties({ entity: 'dubaiotc', type: 'customer', q }),
    enabled: customerSlug.length > 0,
  });
  const counterparties = usePartySearch({
    base: ['parties', 'counterparty'], text: mcSellerSlug,
    ask: (q) => api.listParties({ entity: 'dubaiotc', type: 'counterparty', q }),
    enabled: mcSellerSlug.length > 0,
  });
  const treasury = useQuery({
    queryKey: ['treasury', 'balances'],
    queryFn:  () => api.treasuryBalances('dubaiotc'),
  });

  const customerOptions: ComboboxOption[] = useMemo(() =>
    rankForQuery(
      (customers.data?.parties ?? []).filter((p) => p.party_type !== 'own_account'),
      customerSlug,
    )
      .map((p) => ({
        value:     p.slug,
        label:     partyName(p),
        secondary: partySubtitle(p, { compact: true }),
        stale:     isStale(p),
        review:    needsNameReview(p.display_name),
      }))
  , [customers.data, customerSlug]);
  // The typed text is a party only once it matches one (NewTrade's partyExists
  // idea); from then on the pay-in picker leads with that customer's own rails
  // (12 Sept 2026), never with a half-typed name's.
  const customerKnown = useMemo(
    () => (customers.data?.parties ?? []).some((p) => p.slug === customerSlug),
    [customers.data, customerSlug],
  );

  const sellerOptions: ComboboxOption[] = useMemo(() =>
    (counterparties.data?.parties ?? [])

      .map((p) => ({
        value:     p.slug,
        label:     partyName(p),
        secondary: partySubtitle(p, { compact: true }),
        stale:     isStale(p),
        review:    needsNameReview(p.display_name),
      }))
  , [counterparties.data]);

  // ── live math (all cheque amounts are AED-denominated) ─────────────────
  const face          = Number(faceAmount) || 0;
  // BASIS POINTS ARE WHOLE. `admin.deals.mc_customer_fee_bps` and
  // `mc_seller_fee_bps` are integer columns, and delivery truncates whatever it
  // is handed before charging the cheque, so a fraction would price the deal
  // on this screen at one rate and post it at another — the same gap stage
  // 3's fee box carried until 3 Sept. The box takes PERCENT (0.80) and shows
  // the bps it becomes (= 80 bps); the round happens once, here, where the
  // operator can still see the figure the deal is booked with.
  const custFeeBpsN   = Math.round((Number(customerFeePct) || 0) * 100);
  const selFeeBpsN    = Math.round((Number(sellerFeePct) || 0) * 100);
  const depPctN       = Number(depositPct) || 0;
  const customerFee   = face * custFeeBpsN / 10000;    // AED — commission ON the cheque value
  const sellerFee     = face * selFeeBpsN  / 10000;    // AED
  // ── WHAT THE CUSTOMER OWES ────────────────────────────────────────────────
  // The commission is ADDED ON the cheque value (operator, 2026-08-24: "+0.8%
  // should be added on the cheque value"), so the customer's obligation is
  // face + commission — and the booking deposit is a percentage of THAT, not of
  // the bare face.
  //
  // Until now this line read `face * depPctN / 100`, which quoted a deposit
  // 0.8% light: on a 2,372,278 face it asked for 12,981.00 USDT where the
  // operator had already collected 13,085. Booking the app's figure would have
  // left the wallet 103.85 USDT short of the coins that actually arrived, and
  // the chain leg would have carried the difference as an unexplained gap.
  //
  // The customer's TOTAL is unchanged either way (face + commission), and so is
  // the desk's spread — only the split between deposit and closing balance
  // moves, which is why the error was quiet.
  const customerOwes  = face + customerFee;            // AED
  const depositAmt    = customerOwes * depPctN / 100;  // AED — the computed figure
  const sellerPaysLater = face + sellerFee;
  const spread        = customerFee - sellerFee;

  // The deposit rail's asset is not AED: the three-way group is on screen and
  // every deposit figure below is read from it, never recomputed here.
  const depositIsFx   = customerRail.asset !== 'AED';
  const fxRateN       = Number(math.rate) || 0;
  // The AED deposit that will be booked: the group's AED box (prefilled with
  // the computed figure, possibly corrected by the operator) — or, on an AED
  // rail, the computed figure itself.
  const depositAedN   = depositIsFx ? (Number(math.amountOf('AED')) || 0) : depositAmt;
  const depositInRail = depositIsFx ? (Number(math.amountOf(customerRail.asset)) || 0) : depositAmt;
  // A corrected AED deposit changes what is left for stage 3.
  const balanceLater  = customerOwes - depositAedN;    // AED
  // The stage-3 balance is only an estimate at today's rate (it is set at
  // collection). It is a fourth number the group does not hold, so it reads
  // the rate the way the pair does: AED × "INR per AED", AED ÷ "AED per USDT".
  const aedIsBase     = foldAsset(math.base) === 'AED';
  const balanceInRail = depositIsFx
    ? (fxRateN > 0 ? (aedIsBase ? balanceLater * fxRateN : balanceLater / fxRateN) : 0)
    : balanceLater;
  // WHAT THE DEPOSIT IS (30 Sept 2026): its share of face + commission, what
  // it leaves for stage 3, whether it is the whole cheque — derived from the
  // AED actually put down, never read off the prefill box. The prefill is a
  // seed; this is the fact; every line below reads the fact. See
  // lib/mc-deposit.ts for why the two had to be separated.
  const dep = depositFacts(customerOwes, depositAedN);

  // 21 Oct 2026 audit: `depositKnown` is gone with the line it served. On 30
  // Sept 2026 (Mechanisms/Astra review) a deliberately typed "0" deposit had to
  // read as KNOWN — depositFacts() cannot tell a typed "0" from an untouched
  // box, and the fact line "Deposit X · N% of Y face + commission · leaves Z
  // for stage 3" showed nothing for it. That line said the deposit a third
  // time (the boxes, that line, the summary card) and is deleted; the card
  // carries the same figures once, and a zero deposit reads there as the
  // whole amount still to collect (Balance). The paid-in-full and paid-over
  // lines below stay: they say what the card does not.

  // Prefill: face × % lands in the AED box through preset() so it counts as
  // typed first (a pasted rate then moves the coin box, never this one). Runs
  // again whenever face, %, or the customer fee changes — until the operator
  // has typed in the AED box. A blank face blanks the box, and the calculator
  // blanks whatever it had derived from it.
  //
  // math.preset STAYS in the deps (12 Sept 2026, after preset() became
  // idempotent). It changes identity when the rail's asset changes, and the
  // re-run is then a no-op — the AED figure has already followed its asset to
  // its new box — with one exception worth keeping: an AED box the operator
  // blanked is filled again on a rail change, which is the "next change" the
  // note on depositTyped promises. Dropping the dep would leave that box empty
  // until face or % moved.
  //
  // NOT WHEN THE COINS CAME FROM THE CHAIN: the locked coin figure is the
  // first fact, and a prefilled AED would be the second — the rate would then
  // derive from face × % ÷ coins, which is nobody's rate. The operator types
  // the rate (or the AED actually agreed) and the other box fills.
  useEffect(() => {
    if (!depositIsFx || depositTyped.current || chainLocked) return;
    math.preset({ asset: 'AED' }, depositAmt > 0 ? fmtQty(depositAmt, 'AED') : '');
  }, [depositAmt, depositIsFx, math.preset, chainLocked]);
  // The chain's coins go in once, as if typed first (NewTrade's rule for
  // ?take=). Mount only: the URL does not change while the screen is open.
  useEffect(() => {
    if (chainLocked && chainRail) math.preset({ asset: chainRail.asset }, chainTake);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const feeError = custFeeBpsN < selFeeBpsN ? 'Customer fee must be ≥ seller fee.' : null;
  const fxError = depositIsFx && !(depositAedN > 0 && fxRateN > 0 && depositInRail > 0)
    ? `Fill any two: Deposit in AED, Rate, Deposit in ${displayAsset(customerRail.asset)}.`
    : null;

  // ── submit ─────────────────────────────────────────────────────────────
  // ONE ID PER FORM FILL. The route dedupes on `${deal_id}:book`, but this
  // screen used to send no id, so the server minted a fresh one every attempt:
  // when a response was lost (tunnel drop, Worker timeout) the operator saw an
  // error, tapped again, and the customer carried TWO deposits under two deals
  // (3 Sept audit). A stable id makes the retry land on the same reference.
  const [dealId, setDealId] = useState(() => newChequeId());
  // ASTRA'S REVIEW STEP (UI only): "Review deposit" opens the readback; the next press books. It never posts.
  const [reviewing, setReviewing] = useState(false);
  // ONE CHAIN TRANSFER, BOOKED ONCE (27 Oct 2026, Mechanisms' 9d9756c8): a cheque opened from a Chain row carries the
  // transfer's hash in its note and the wallet it landed on as the deposit's rail. When the books already hold that
  // transfer there, the route answers 409 repeat_suspected with the twin — the charge screen's repeat panel, here: go
  // back, or post it again anyway (confirm_repeat), whatever the press that met it also carried.
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();
  const submit = useMutation({
    mutationFn: async (opts?: {
      profile_kind?: 'tracked' | 'treasury_only';
      mc_seller_role?: 'merchant' | 'other';
      confirm_repeat?: boolean;
    }) => {
      try {
        return await guard(() => api.bookManagerCheque({
          entity:                 'dubaiotc',
          deal_id:                dealId,
          customer_slug:          customerSlug,
          customer_display_name:  customerSlug,
          profile_kind:           opts?.profile_kind,
          mc_face_amount:         face,
          mc_seller_slug:         mcSellerSlug,
          mc_seller_display_name: mcSellerSlug,
          mc_seller_role:         opts?.mc_seller_role,
          customer_treasury_slug: customerRail.slug,
          customer_pay_asset:     customerRail.asset,
          // To the rail's own precision. Rounding to a WHOLE unit here quoted the
          // operator one figure and booked another — on a coin rail that left the
          // wallet short by up to a coin, and the chain leg carried the gap.
          customer_deposit_amount: Number(depositInRail.toFixed(isFiat(customerRail.asset) ? 2 : 6)),
          // THE SHARE PAID, not the prefill (30 Sept 2026): 44.38 for a
          // 1,624,013.27 deposit on a 3,659,613.55 cheque, whatever the seed
          // box says. The route derives the same figure from mc_deposit_aed
          // and stores THAT; this one serves a route that predates the field.
          mc_deposit_pct:         Number(dep.sharePct.toFixed(4)),
          // The deposit in AED and the rate that valued it, so the books hold
          // what the coins were worth on the day. Until now a USDT deposit was
          // booked with no AED figure and no rate, and its share could not be
          // recovered afterwards (MC-2026-09-30-1ULLM8 is one such).
          mc_deposit_aed:         Number(depositAedN.toFixed(2)),
          mc_deposit_rate_to_aed: depositIsFx && depositInRail > 0 ? Number((depositAedN / depositInRail).toFixed(8)) : undefined,
          mc_customer_fee_bps:    custFeeBpsN,
          mc_seller_fee_bps:      selFeeBpsN,
          // The note carries the three figures as they stood on screen — the
          // rate with its words, since "INR per AED" and "AED per USDT" read
          // opposite ways and a bare number would not say which.
          notes:                  depositIsFx
            ? [`Deposit FX: ${fmtAmount(depositAedN, 'AED')} AED @ ${fxRateN} ${math.rateWords} = ${fmtAmount(depositInRail, customerRail.asset)} ${displayAsset(customerRail.asset)}`, notes].filter(Boolean).join(' · ')
            : (notes || undefined),
          confirm_repeat:         opts?.confirm_repeat || undefined,
        }));
      } catch (e) {
        // The old try/catch{} here swallowed its own needs_confirm rethrow, so
        // a new party or seller surfaced as the raw API error (3 Sept audit).
        const body = e instanceof ApiError && e.status === 409
          ? (e.body as { needs_confirm?: string; suggested?: { slug: string; display_name: string } }) : null;
        if (body?.needs_confirm === 'create_party' && body.suggested) {
          setPending({ slug: body.suggested.slug, display_name: body.suggested.display_name });
          throw new Error('needs_confirm');
        }
        if (body?.needs_confirm === 'create_mc_seller' && body.suggested) {
          setPendingSeller({ slug: body.suggested.slug, display_name: body.suggested.display_name });
          throw new Error('needs_confirm');
        }
        throw e;
      }
    },
    onMutate: () => clearTwin(),
    onSuccess: (data) => {
      tg()?.HapticFeedback.notificationOccurred('success');
      setPending(null);
      setPendingSeller(null);
      setDealId(newChequeId());   // the next cheque is its own deal
      // The seller fee just used becomes the next cheque's default (§5.3).
      try { localStorage.setItem(SELLER_FEE_PCT_KEY, sellerFeePct); } catch { /* no storage — nothing to remember */ }
      // Single flow: after booking, land the operator on the deal's lifecycle screen
      // The progress page is told it was just booked here, so a first read that fails there still says the cheque was booked
      // and names it (1 Nov 2026 audit: the page showed only "Couldn't read this cheque").
      if (data?.deal_id) nav(`/mc/${data.deal_id}`, { state: { justBooked: data.deal_id } });
    },
    onError:   (e) => { if ((e as Error).message !== 'needs_confirm') tg()?.HapticFeedback.notificationOccurred('error'); },
  });

  const canSubmit = !submit.isPending && !!customerSlug && face > 0
                  && !!customerRail.slug && !!mcSellerSlug && !feeError && !fxError
                  && !pendingProfileChoice && !pendingSellerChoice && !repeat;

  const asset = customerRail.asset;
  // The summary card (stays, §5.3) — the shared KeyValue, without the two
  // separator rows; the stage rides under the label as its qualifier. Its
  // rows are built by chequeSummaryRows (above) from this screen's figures.
  const summary = chequeSummaryRows({
    customerOwes, depositIsFx, fxError, asset, rateWords: math.rateWords, fxRateN,
    depositAmt, depositAedN, depositInRail, dep, depPctN,
    sellerPaysLater, balanceLater, balanceInRail, spread,
  });

  // ── THE V2 DRAWING ───────────────────────────────────────────────────────────────────────────────────────────────
  const treasuryRead = readState(treasury);
  // THE FOOT SAYS WHAT IS STILL MISSING (Astra: "identify the missing prerequisite") — the first one, in a few words.
  const missing = !customerSlug ? 'Pick the customer.'
    : !mcSellerSlug ? 'Pick the seller.'
    : !(face > 0) ? 'Enter the face amount.'
    : !customerRail.slug ? 'Pick where the deposit lands.'
    : feeError ? feeError
    : fxError ? 'Fill the rate or the coin deposit.'
    : pendingProfileChoice || pendingSellerChoice || repeat ? 'Answer the question above.'
    : null;
  const nameOf = (slug: string, opts: ComboboxOption[]) => opts.find((o) => o.value === slug)?.label ?? slug;
  // A fee in percent, with the whole basis points the deal is charged at beside it — the hint, not a second box.
  const feeHint = (bps: number) => `= ${bps} bps`;

  // THE REVIEW — who, where, the fees as charged, then the summary card's five rows (ManagerCheque's own helper).
  const reviewRows: KeyValueItem[] = [
    { label: 'Customer', value: nameOf(customerSlug, customerOptions) },
    { label: 'Seller', value: nameOf(mcSellerSlug, sellerOptions) },
    { label: 'Lands in', value: railWord(treasury.data?.rows ?? [], customerRail) },
    { label: 'Fees', value: <><span className="whitespace-nowrap">customer {custFeeBpsN} bps</span> · <span className="whitespace-nowrap">seller {selFeeBpsN} bps</span></> },
    ...summary,
  ];
  const review = reviewing && face > 0 ? (
    <div className="space-y-3">
      <KeyValue rows={reviewRows} className="max-lg:gap-x-2" />
      {/* WHAT THE DEPOSIT IS when it is the whole cheque, or more (30 Sept / 22 Oct 2026): on a COIN rail "paid in full" is
          made at the booking rate and stage 3 re-values the coins — not final, so not green; on an AED rail it is. */}
      {dep.paidInFull && !(dep.over > 0) && (
        depositIsFx ? (
          <StateLine
            more={<>{fmtAmount(depositAedN, 'AED', 2)} AED covers face + commission at {fxRateN} {math.rateWords}. Stage 3 re-values the coins at the rate set at collection; if that rate is {aedIsBase ? 'higher' : 'lower'}, a balance remains.</>}
          >
            Paid in full at the booking rate — balance may remain.
          </StateLine>
        ) : (
          <StateLine tone="good" more={<>{fmtAmount(depositAedN, 'AED', 2)} AED covers face + commission.</>}>
            Paid in full — nothing due at stage 3.
          </StateLine>
        )
      )}
      {dep.over > 0 && (
        <StateLine
          tone="warn"
          more={depositIsFx
            ? <>Paid in full at {fxRateN} {math.rateWords}, {fmtAmount(dep.over, 'AED', 2)} AED more than face + commission at that rate — the excess moves with the collection rate.</>
            : <>Paid in full, and {fmtAmount(dep.over, 'AED', 2)} AED more than face + commission — the excess stays on the customer&rsquo;s box as theirs.</>}
        >
          {fmtAmount(dep.over, 'AED', 2)} AED over face + commission — the excess is the customer&rsquo;s.
        </StateLine>
      )}
    </div>
  ) : undefined;

  // EVERY ANSWER OPENS ABOVE THE PRESS (§7 rule 11). Creating a party or a seller IS the re-POST of the cheque, so the
  // buttons lock while one is in flight (Book trade's one-tap rule) — ManagerCheque's panels, as they were.
  const answers = (pendingProfileChoice || pendingSellerChoice || repeat || (submit.isError && !isSentinel(submit.error))) ? (
    <>
      {pendingProfileChoice && (
        <div className="border border-tg-accent/40 rounded p-3 space-y-2 bg-tg-section-bg">
          <p className="text-body">
            <span className="font-semibold">{pendingProfileChoice.display_name}</span> doesn&rsquo;t exist yet — create as:
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="btn-secondary text-body py-2 text-left px-3 disabled:opacity-40" disabled={submit.isPending} onClick={() => submit.mutate({ profile_kind: 'tracked' })}>
              <span className="font-medium">{submit.isPending && submit.variables?.profile_kind === 'tracked' ? 'Booking…' : 'Tracked'}</span>
              <span className="block text-meta text-tg-hint">KYC required</span>
            </button>
            <button type="button" className="btn-secondary text-body py-2 text-left px-3 disabled:opacity-40" disabled={submit.isPending} onClick={() => submit.mutate({ profile_kind: 'treasury_only' })}>
              <span className="font-medium">{submit.isPending && submit.variables?.profile_kind === 'treasury_only' ? 'Booking…' : 'Treasury-only'}</span>
              <span className="block text-meta text-tg-hint">No KYC; name only</span>
            </button>
          </div>
          <button type="button" className="btn-link text-meta text-tg-hint disabled:opacity-40" disabled={submit.isPending} onClick={() => setPending(null)}>Cancel</button>
        </div>
      )}
      {pendingSellerChoice && (
        <div className="border border-tg-accent/40 rounded p-3 space-y-2 bg-tg-section-bg">
          <p className="text-body">
            Seller <span className="font-semibold">{pendingSellerChoice.display_name}</span> doesn&rsquo;t exist yet — create as:
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="btn-secondary text-body py-2 disabled:opacity-40" disabled={submit.isPending} onClick={() => submit.mutate({ mc_seller_role: 'merchant' })}>
              {submit.isPending && submit.variables?.mc_seller_role === 'merchant' ? 'Booking…' : 'Merchant'}
            </button>
            <button type="button" className="btn-secondary text-body py-2 disabled:opacity-40" disabled={submit.isPending} onClick={() => submit.mutate({ mc_seller_role: 'other' })}>
              {submit.isPending && submit.variables?.mc_seller_role === 'other' ? 'Booking…' : 'Other'}
            </button>
          </div>
          <button type="button" className="btn-link text-meta text-tg-hint disabled:opacity-40" disabled={submit.isPending} onClick={() => setPendingSeller(null)}>Cancel</button>
        </div>
      )}
      {repeat && (
        <RepeatPanel
          twin={repeat}
          pending={submit.isPending}
          onAlreadyBooked={() => { clearTwin(); nav(-1); }}
          onPostAnyway={() => submit.mutate({ ...submit.variables, confirm_repeat: true })}
        />
      )}
      {submit.isError && !isSentinel(submit.error) && <ServerLine error={submit.error} />}
    </>
  ) : undefined;

  // The success block stays gone (§5.3): a booked cheque navigates to its own page at once.
  const primary = (
    <button
      className="btn-primary w-full lg:w-auto lg:px-6"
      disabled={!canSubmit}
      onClick={() => { if (reviewing) submit.mutate(undefined); else setReviewing(true); }}
    >
      {submit.isPending ? 'Booking…' : reviewing ? 'Book deposit' : 'Review deposit →'}
    </button>
  );

  const depositAedBox = (
    <input
      className={`input${math.isDerived('AED') ? ' text-tg-hint' : ''}`}
      value={math.amountOf('AED')}
      onChange={(e) => {
        // Typing here makes the AED figure the operator's own; a blank hands it back to face × % on the next change.
        depositTyped.current = e.target.value.trim() !== '';
        math.setAmount('AED', e.target.value);
      }}
      inputMode="decimal"
      aria-label="Deposit in AED"
    />
  );

  const card = (
    <FormCard review={review} answers={answers} primary={primary} note={missing && !submit.isPending ? missing : null}>
      <FormGrid>
        <Field label="Customer" className="max-lg:col-span-2">
          <Combobox
            value={customerSlug}
            onChange={setCustomerSlug}
            options={customerOptions}
            more={rosterCut(customers.data)}
            placeholder="Search party"
            // a read in flight OR waiting for the network (a first read opened offline is paused): "…", not "No party by that name"
            loading={customers.isFetching || customers.fetchStatus === 'paused'}
            emptyHint={customerSlug.length === 0 ? null : NO_PARTY_CREATE_HINT}
            // A SEARCH THAT FAILED IS NOT "NO PARTY BY THAT NAME": the failed line, with its Try again, is inside the open list.
            readFailed={customers.isError ? <ReadFailed what="the parties" error={customers.error} onRetry={() => void customers.refetch()} /> : undefined}
          />
        </Field>
        <Field label="Seller" className="max-lg:col-span-2">
          <Combobox
            value={mcSellerSlug}
            onChange={setMcSeller}
            options={sellerOptions}
            more={rosterCut(counterparties.data)}
            placeholder="Search party"
            loading={counterparties.isFetching || counterparties.fetchStatus === 'paused'}
            emptyHint={mcSellerSlug.length === 0 ? null : NO_PARTY_CREATE_HINT}
            readFailed={counterparties.isError ? <ReadFailed what="the parties" error={counterparties.error} onRetry={() => void counterparties.refetch()} /> : undefined}
          />
        </Field>

        <Field label="Face amount (AED)">
          <input className="input" value={faceAmount} onChange={(e) => setFaceAmount(e.target.value)} inputMode="decimal" aria-label="Face amount" />
        </Field>
        {/* THE DEPOSIT. On a coin rail it is the AED actually agreed — prefilled from the percent under More until he types
            it. On an AED rail the cheque's deposit IS face + commission × the percent, so the percent is the box. */}
        {depositIsFx ? (
          <Field label="Deposit (AED)" hint={math.isDerived('AED') ? 'auto' : undefined}>{depositAedBox}</Field>
        ) : (
          <Field label="Deposit (%)">
            <input className="input" value={depositPct} onChange={(e) => setDepositPct(e.target.value)} inputMode="decimal" aria-label="Deposit percent" />
          </Field>
        )}
        {/* THE DEPOSIT'S USUAL SEEDS AND WHERE THE CHEQUE STANDS, live (restored 1 Nov 2026 — the old screen had both; the
            rebuild had put the chips away and the summary into the review). One chip row, one quiet line. */}
        <div className="col-span-2 space-y-2">
          <div role="group" aria-label="Deposit at" className="flex flex-wrap gap-2">
            {PREFILL_CHIPS.map(([v, words]) => (
              <button key={v} type="button" aria-pressed={depositPct === v} disabled={chainLocked}
                className={`chip min-h-[44px] px-3 lg:min-h-[40px] ${depositPct === v ? 'chip-on' : ''} disabled:opacity-50`}
                onClick={() => { depositTyped.current = false; setDepositPct(v); }}>{words}</button>
            ))}
          </div>
          {/* Three labelled rows, not a sentence (1 Nov 2026 audit #6): the figure at the right, its unit muted. */}
          {face > 0 && customerOwes > 0 && (
            <div className="space-y-1">
              <FigureRow label="Customer total" value={customerOwes} asset="AED" />
              <FigureRow label="Deposit" value={depositAedN} asset="AED" />
              <FigureRow label="Balance" value={Math.max(0, customerOwes - depositAedN)} asset="AED" strong />
            </div>
          )}
        </div>

        <Field label="Deposit lands in" className="max-lg:col-span-2">
          <RailPicker balanceBelow
            value={customerRail}
            onChange={setCustomerRail}
            rows={treasury.data?.rows ?? []}
            // MC_PAY_IN_KINDS, not a literal: the same list is enforced server-side.
            allowedKinds={MC_PAY_IN_KINDS}
            partySlug={customerKnown ? customerSlug : undefined}
            loading={treasury.isFetching || treasury.fetchStatus === 'paused'}
            readFailed={treasury.isError}
            placeholder="Pay-in account"
          />
          {treasury.isError && <ReadFailed what="the accounts" error={treasury.error} onRetry={() => void treasury.refetch()} className="mt-1" />}
        </Field>
        <Field label="Customer fee (%)" hint={feeHint(custFeeBpsN)} className="max-lg:col-span-2">
          <input className="input" value={customerFeePct} onChange={(e) => setCustomerFeePct(e.target.value)} inputMode="decimal" aria-label="Customer fee %" />
        </Field>
        <Field label="Seller fee (%)" hint={feeHint(selFeeBpsN)} className="max-lg:col-span-2">
          <input className="input" value={sellerFeePct} onChange={(e) => setSellerFeePct(e.target.value)} inputMode="decimal" aria-label="Seller fee %" />
        </Field>
        {feeError && <ErrorLine className="col-span-2 mt-0">{feeError}</ErrorLine>}

        {/* A DEPOSIT IN COINS: Rate and the coin figure — any two of the three fill the third (the shared calculator). Opened
            from a Chain row the coins are what the chain brought and cannot be typed over. */}
        {depositIsFx && (
          <>
            {/* Rate and the coin figure share their own row on the laptop (the seller fee's row keeps its empty half). */}
            {!feeError && <div className="max-lg:hidden" aria-hidden />}
            <Field label="Rate" hint={math.rateIsDerived ? `${math.rateWords} · auto` : math.rateWords} className="max-lg:col-span-2">
              <input
                className={`input${math.rateIsDerived ? ' text-tg-hint' : ''}`}
                value={math.rate}
                onChange={(e) => math.setRate(e.target.value)}
                inputMode="decimal"
                aria-label="Rate"
              />
            </Field>
            <Field label={`Deposit (${displayAsset(asset)})`}
              hint={chainLocked ? 'from chain' : math.isDerived(asset) ? 'auto' : undefined} className="max-lg:col-span-2">
              <input
                className={`input${math.isDerived(asset) ? ' text-tg-hint' : ''}${chainLocked ? ' bg-tg-bg/30' : ''}`}
                value={math.amountOf(asset)}
                onChange={(e) => { if (!chainLocked) math.setAmount(asset, e.target.value); }}
                readOnly={chainLocked}
                aria-readonly={chainLocked || undefined}
                inputMode="decimal"
                aria-label={`Deposit in ${displayAsset(asset)}`}
              />
            </Field>
            {fxError && <ErrorLine className="col-span-2 mt-0">{fxError}</ErrorLine>}
          </>
        )}
      </FormGrid>

      {/* Percentage presets, the explanation and notes are behind a tap (Astra). The percent seeds a coin deposit's AED
          box; it is inert when the coins came from a chain row. */}
      <div className="border-t border-stroke-soft pt-4">
        <MoreDetails label="More">
          {depositIsFx && (
            <Field label="Prefill at %" hint={chainLocked ? 'coins came from the chain' : undefined}>
              <input className="input w-24" value={depositPct} onChange={(e) => setDepositPct(e.target.value)} inputMode="decimal" disabled={chainLocked} aria-label="Prefill deposit percent" />
            </Field>
          )}
          <Field label="Notes">
            <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
          </Field>
        </MoreDetails>
      </div>
    </FormCard>
  );

  // About this job: the explanation, the desk and when the books were read — beside the card, and under it on the
  // phone (Astra, 1 Nov 2026).
  const help = (
    <>
      <p>
        Stage 1 of 3. The customer pays a deposit now and the cheque is booked; the seller is paid and the cheque
        handed over later, from the deal&rsquo;s own page (Home → Open cheques).
      </p>
      <p>
        The commission is added on the cheque value, so the deposit is a share of face + commission; what is left
        is taken at handover — on a coin deposit only an estimate until the rate is set at collection.
      </p>
      <p>
        Fees are in percent and the deal carries them as whole basis points. The seller fee opens on the last one
        used. On a coin deposit, any two of Deposit (AED), Rate and the coin figure fill the third.
      </p>
    </>
  );
  const facts = [{ label: 'Desk', value: 'Dubai' }, { label: 'Read', value: treasuryRead === 'known' ? fmtTime(treasury.dataUpdatedAt) : '—' }];

  return (
    <>
      <PageHead title="Manager's cheque" />
      <FormPage
        card={card}
        below={<AboutFold help={help} facts={facts} />}
        // The column only while there is a readback; before it the explanation sits under the form (1 Nov 2026 audit, P7).
        aside={review ? (
          <BeforeConfirming
            review={review}
            help={help}
            facts={facts}
          />
        ) : null}
      />
    </>
  );
}
